import { PropertyType } from '@beatflyer/shaders-lib';
import { CustomPropertyRow } from 'controller/CustomPropertyRow';
import { CustomProperty } from 'data/CustomProperty';
import {
  isOvalShape,
  isRectangleShape,
  PolygonShapeData,
  ShapeData,
  SimpleShapeData
} from 'data/FlyerData';
import * as React from 'react';
import { cloneObject } from 'utils';
import { MultiColorRow } from '../components/MultiColorRow';
import {
  FrameSettingsPopover,
  FrameSettingsPopoverProps
} from './FrameSettingsPopover';

interface Props extends FrameSettingsPopoverProps {
  data: ShapeData;
  colors: string[];
  onChange: (colors: string[]) => void;
  onShapeChange: (data: ShapeData) => void;
  onEditShape: () => void;
}

export const ShapeSettingsPopover: React.FC<Props> = ({
  data,
  colors,
  onChange,
  onShapeChange,
  onEditShape,
  trigger,
  onClose
}) => {
  const addColor = React.useCallback(() => {
    const lastColor = colors[colors.length - 1];
    onChange(colors.concat([lastColor]));
  }, [colors, onChange]);

  const removeColor = React.useCallback(
    (index: number) => {
      const newColors = colors.concat([]);
      newColors.splice(index, 1);
      onChange(newColors);
    },
    [colors, onChange]
  );

  const colorDidChange = React.useCallback(
    (index: number, color: string) => {
      const newColors = colors.concat([]);
      newColors.splice(index, 1, color);
      onChange(newColors);
    },
    [colors, onChange]
  );

  const isRectangle = isRectangleShape(data);
  const isOval = isOvalShape(data);
  const isPolygon = 'corners' in data;
  const cornerRadiusProperty: CustomProperty = {
    name: 'Corner',
    type: PropertyType.INT,
    minValue: 0,
    maxValue: 100,
    value: (data as SimpleShapeData).cornerRadius ?? 0
  };
  const cornersAmountProperty: CustomProperty = {
    name: 'Corners',
    type: PropertyType.INT,
    minValue: 3,
    maxValue: 10,
    value: (data as PolygonShapeData).corners
  };

  return (
    <FrameSettingsPopover
      className="shape-settings-popover"
      trigger={trigger}
      onClose={onClose}
    >
      {!isOval && (
        <div className="custom-property">
          <label>Shape</label>
          <button
            onClick={() => {
              onEditShape();
              onClose();
            }}
          >
            Edit shape
          </button>
        </div>
      )}
      {isRectangle && (
        <CustomPropertyRow
          name={cornerRadiusProperty.name}
          id={cornerRadiusProperty.name}
          value={cornerRadiusProperty.value}
          data={cornerRadiusProperty}
          onChange={(_p, value) => {
            const newRectable = cloneObject(data);
            (newRectable as SimpleShapeData).cornerRadius = value;
            onShapeChange(newRectable);
          }}
        />
      )}
      {isPolygon && (
        <CustomPropertyRow
          name={cornersAmountProperty.name}
          id={cornersAmountProperty.name}
          value={cornersAmountProperty.value}
          data={cornersAmountProperty}
          onChange={(_p, value) => {
            const newPolygon = cloneObject(data);
            (newPolygon as PolygonShapeData).corners = Math.round(value);
            onShapeChange(newPolygon);
          }}
        />
      )}
      <MultiColorRow
        color={colors}
        onChange={colorDidChange}
        addColor={addColor}
        removeColor={removeColor}
        compact={false}
      />
    </FrameSettingsPopover>
  );
};



// WEBPACK FOOTER //
// ./src/controller/settings-popovers/ShapeSettingsPopover.tsx